import React, { useMemo } from "react";
import { IMaskInput } from "react-imask";
import { formatAmount } from "../../../OperationsJournal/mock/operationsJournalMock";
import SelectionBar from "../SelectionBar/SelectionBar";
import AllocationInfo from "../AllocationInfo/AllocationInfo";
import MultiSelect from "../MultiSelect/MultiSelect";
import { BASE_ARTICLES } from "../../mock/allocationBaseMock";
import {
  BASE_METHODS,
  BASE_METHOD_BY_ID,
  calculateAllocation,
  formatPercent,
  getEqualPercents,
  isArticleMethod,
} from "../../utils/allocationBase";
import "./BaseStep.css";

const PencilIcon = () => (
  <svg
    className="al-share-input__icon"
    width="14"
    height="14"
    viewBox="0 0 14 14"
    fill="none"
    aria-hidden
  >
    <path
      d="M8.6 2.6l2.8 2.8M2 12l.6-2.9 7.1-7.1a1.4 1.4 0 012 0l.3.3a1.4 1.4 0 010 2L4.9 11.4 2 12z"
      stroke="currentColor"
      strokeWidth="1.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/**
 * Шаг 3: выбор базы распределения и предварительный расчёт долей.
 * value — { method, articles: { opiu, dds, balance }, manualShares: { [projectId]: "50,5" } }.
 */
function BaseStep({
  projects,
  totalAmount,
  operationsCount,
  value,
  onChange,
  onBack,
  onContinue,
}) {
  const { method } = value;
  const methodConfig = BASE_METHOD_BY_ID[method];
  const withArticles = isArticleMethod(method);
  const isManual = method === "manual";

  const allocation = useMemo(
    () => calculateAllocation(projects, totalAmount, value),
    [projects, totalAmount, value]
  );

  const selectMethod = (nextMethod) => {
    if (nextMethod !== method) onChange({ ...value, method: nextMethod });
  };

  const fillEqually = () =>
    onChange({
      ...value,
      manualShares: { ...value.manualShares, ...getEqualPercents(projects) },
    });

  const setArticles = (articles) =>
    onChange({ ...value, articles: { ...value.articles, [method]: articles } });

  const setManualShare = (projectId, share) =>
    onChange({ ...value, manualShares: { ...value.manualShares, [projectId]: share } });

  const showPreview = !withArticles || value.articles[method].length > 0;
  const hasShareError = isManual && Boolean(allocation.error);

  return (
    <div className="al-base">
      <h3 className="al-section-title">Выберите базу распределения</h3>

      <AllocationInfo
        totalLabel={formatAmount(totalAmount)}
        operationsCount={operationsCount}
      />

      <div className="al-methods" role="radiogroup" aria-label="База распределения">
        {BASE_METHODS.map((item) => (
          <label
            key={item.id}
            className={`al-method${method === item.id ? " is-active" : ""}`}
          >
            <input
              type="radio"
              name="allocation-base"
              className="al-method__radio"
              checked={method === item.id}
              onChange={() => selectMethod(item.id)}
            />
            <span className="al-method__text">
              <span className="al-method__title">{item.title}</span>
              <span className="al-method__description">{item.description}</span>
            </span>
          </label>
        ))}
      </div>

      {withArticles && (
        <div className="al-base__articles">
          <MultiSelect
            key={method}
            label={methodConfig.articlesLabel}
            placeholder="Выберите статьи"
            options={BASE_ARTICLES[method]}
            value={value.articles[method]}
            onChange={setArticles}
          />
          <p className="al-base__hint">
            Доля проекта = сумма его показателей по выбранным статьям / сумма показателей
            всех выбранных проектов.
          </p>
        </div>
      )}

      {showPreview && (
        <div className="al-preview">
          <div className="al-preview__header">
            <span className="al-preview__title">Предварительное распределение</span>
            {isManual && (
              <button type="button" className="al-link" onClick={fillEqually}>
                Распределить поровну
              </button>
            )}
          </div>
          <div className="oj-table-scroll">
            <table className="oj-table al-preview__table">
              <thead>
                <tr>
                  <th>Проект</th>
                  {withArticles && <th className="oj-cell--num">Значение базы (₸)</th>}
                  <th className={isManual ? "al-preview__share-col" : "oj-cell--num"}>
                    Доля
                  </th>
                  <th className="oj-cell--num">Сумма (₸)</th>
                </tr>
              </thead>
              <tbody>
                {allocation.rows.map((row) => (
                  <tr key={row.project.id}>
                    <td>
                      <div className="al-preview__project">
                        <span className="al-preview__code">{row.project.code}</span>
                        <span className="al-preview__name">{row.project.name}</span>
                      </div>
                    </td>
                    {withArticles && (
                      <td className="oj-cell--num">
                        {row.base ? formatAmount(row.base) : <span className="oj-empty">—</span>}
                      </td>
                    )}
                    <td className={isManual ? "al-preview__share-col" : "oj-cell--num"}>
                      {isManual ? (
                        <span
                          className={`al-share-input${hasShareError ? " is-invalid" : ""}`}
                        >
                          <span className="al-share-input__prefix">%</span>
                          <IMaskInput
                            mask={Number}
                            scale={2}
                            radix=","
                            mapToRadix={["."]}
                            min={0}
                            max={100}
                            normalizeZeros
                            value={value.manualShares[row.project.id] || ""}
                            unmask={false}
                            placeholder="0"
                            className="al-share-input__field"
                            aria-label={`Доля проекта ${row.project.code}, %`}
                            onAccept={(share) => setManualShare(row.project.id, share)}
                          />
                          <PencilIcon />
                        </span>
                      ) : (
                        formatPercent(row.share)
                      )}
                    </td>
                    <td className="oj-cell--num oj-cell--amount">
                      {formatAmount(row.amount)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td>Итого</td>
                  {withArticles && (
                    <td className="oj-cell--num">
                      {formatAmount(
                        allocation.rows.reduce((sum, row) => sum + (row.base || 0), 0)
                      )}
                    </td>
                  )}
                  <td
                    className={`${isManual ? "al-preview__share-col" : "oj-cell--num"}${
                      hasShareError ? " is-invalid" : ""
                    }`}
                  >
                    {formatPercent(allocation.totalShare)}
                  </td>
                  <td className="oj-cell--num oj-cell--amount">
                    {formatAmount(
                      allocation.rows.reduce((sum, row) => sum + row.amount, 0)
                    )}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
          {allocation.error && (
            <div className="al-preview__error" role="alert">
              {allocation.error}
            </div>
          )}
        </div>
      )}

      <SelectionBar
        onBack={onBack}
        continueDisabled={Boolean(allocation.error)}
        onContinue={onContinue}
      />
    </div>
  );
}

export default BaseStep;
